import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { FiscalYearSelector } from "../components/fiscal/FiscalYearSelector.js";
import {
  FiscalCard,
  FiscalShell,
  LoadingSkeleton,
  fiscalInputClass,
} from "../components/fiscal/Shell.js";
import { useFiscalSelection } from "../hooks/useFiscalSelection.js";
import { useT } from "../i18n/index.js";
import { formatMoney, formatPctSigned, toNumber } from "../lib/format-fr.js";
import { trpc } from "../lib/trpc.js";

/**
 * F5.9 — Compare 2 revisões de budget lado-a-lado.
 * Mostra inputs efetivos + KPIs projetados com diff destacado.
 */

type BudgetField =
  | "caHt"
  | "achatsConsommes"
  | "autresChargesVariables"
  | "fournituresConsommables"
  | "locationsChargesLocatives"
  | "primesAssurance"
  | "intermediairesHonoraires"
  | "deplacementsReception"
  | "fraisBancaires"
  | "impotsTaxes"
  | "chargesPersonnel"
  | "dotationsAmortissements"
  | "dotationsProvisions"
  | "autresChargesExploitation"
  | "impotSocietes"
  | "subventionsExploitation"
  | "autresProduitsExploitation";

export default function FiscalBudgetCompare() {
  const { companyId, fiscalYearId, setCompanyId, setFiscalYearId } = useFiscalSelection();
  const t = useT();

  const INPUT_LINES: ReadonlyArray<{ key: BudgetField; label: string; group: string }> = [
    { key: "caHt", label: t("fiscalBudgetCompare.lineCaHt"), group: "Aggregate" },
    {
      key: "achatsConsommes",
      label: t("fiscalBudgetCompare.lineAchatsConsommes"),
      group: "Aggregate",
    },
    {
      key: "autresChargesVariables",
      label: t("fiscalBudgetCompare.lineAutresChargesVariables"),
      group: "Aggregate",
    },
    {
      key: "fournituresConsommables",
      label: t("fiscalBudgetCompare.lineFournitures"),
      group: "Charges externes",
    },
    {
      key: "locationsChargesLocatives",
      label: t("fiscalBudgetCompare.lineLocations"),
      group: "Charges externes",
    },
    {
      key: "primesAssurance",
      label: t("fiscalBudgetCompare.linePrimesAssurance"),
      group: "Charges externes",
    },
    {
      key: "intermediairesHonoraires",
      label: t("fiscalBudgetCompare.lineIntermediaires"),
      group: "Charges externes",
    },
    {
      key: "deplacementsReception",
      label: t("fiscalBudgetCompare.lineDeplacements"),
      group: "Charges externes",
    },
    {
      key: "fraisBancaires",
      label: t("fiscalBudgetCompare.lineFraisBancaires"),
      group: "Charges externes",
    },
    { key: "impotsTaxes", label: t("fiscalBudgetCompare.lineImpotsTaxes"), group: "Outras cargas" },
    {
      key: "chargesPersonnel",
      label: t("fiscalBudgetCompare.lineChargesPersonnel"),
      group: "Outras cargas",
    },
    {
      key: "dotationsAmortissements",
      label: t("fiscalBudgetCompare.lineDotationsAmort"),
      group: "Outras cargas",
    },
    {
      key: "dotationsProvisions",
      label: t("fiscalBudgetCompare.lineDotationsProv"),
      group: "Outras cargas",
    },
    {
      key: "autresChargesExploitation",
      label: t("fiscalBudgetCompare.lineAutresChargesExpl"),
      group: "Outras cargas",
    },
    { key: "impotSocietes", label: t("fiscalBudgetCompare.lineIs"), group: "Outras cargas" },
    {
      key: "subventionsExploitation",
      label: t("fiscalBudgetCompare.lineSubventions"),
      group: "Outros produtos",
    },
    {
      key: "autresProduitsExploitation",
      label: t("fiscalBudgetCompare.lineAutresProduits"),
      group: "Outros produtos",
    },
  ];

  const KPI_FIELDS: ReadonlyArray<{ key: string; label: string }> = [
    { key: "margeGlobaleProjected", label: t("fiscalBudgetCompare.kpiMargeGlobale") },
    { key: "vaProjected", label: t("fiscalBudgetCompare.kpiValeurAjoutee") },
    { key: "ebeProjected", label: t("fiscalBudgetCompare.kpiEbe") },
    {
      key: "resultatExploitationProjected",
      label: t("fiscalBudgetCompare.kpiResultatExploitation"),
    },
    { key: "resultatNetProjected", label: t("fiscalBudgetCompare.kpiResultatNet") },
    { key: "cafProjected", label: t("fiscalBudgetCompare.kpiCaf") },
    { key: "seuilEconomiqueProjected", label: t("fiscalBudgetCompare.kpiSeuilEconomique") },
    { key: "seuilFinancierProjected", label: t("fiscalBudgetCompare.kpiSeuilFinancier") },
    { key: "pointMortProjected", label: t("fiscalBudgetCompare.kpiPointMort") },
  ];

  const listQuery = trpc.fiscalBudgets.listByFiscalYear.useQuery(
    {
      companyId: companyId ?? 0,
      fiscalYearId: fiscalYearId ?? 0,
      includeArchived: false,
    },
    { enabled: companyId !== null && fiscalYearId !== null, retry: false },
  );
  const revisions = listQuery.data ?? [];

  const [leftId, setLeftId] = useState<number | null>(null);
  const [rightId, setRightId] = useState<number | null>(null);

  // Auto-seleciona: esquerda = ativa, direita = qualquer outra (ou null)
  useEffect(() => {
    if (revisions.length === 0) return;
    const active = revisions.find((r) => r.isActive);
    if (leftId === null) setLeftId(active?.id ?? revisions[0]?.id ?? null);
    if (rightId === null) {
      const other = revisions.find((r) => r.id !== (active?.id ?? revisions[0]?.id));
      setRightId(other?.id ?? null);
    }
  }, [revisions, leftId, rightId]);

  const left = useMemo(() => revisions.find((r) => r.id === leftId) ?? null, [revisions, leftId]);
  const right = useMemo(
    () => revisions.find((r) => r.id === rightId) ?? null,
    [revisions, rightId],
  );

  const toolbar = (
    <FiscalYearSelector
      companyId={companyId}
      fiscalYearId={fiscalYearId}
      onChangeCompany={setCompanyId}
      onChangeFiscalYear={(fy) => setFiscalYearId(fy?.id ?? null)}
    />
  );

  if (companyId === null || fiscalYearId === null) {
    return (
      <FiscalShell subtitle={t("fiscalBudgetCompare.subtitle")} toolbar={toolbar}>
        <FiscalCard>
          <p className="text-sm text-neutral-400">{t("fiscalBudgetCompare.selectCompanyYear")}</p>
        </FiscalCard>
      </FiscalShell>
    );
  }

  if (listQuery.isLoading) {
    return (
      <FiscalShell subtitle={t("fiscalBudgetCompare.subtitle")} toolbar={toolbar}>
        <FiscalCard>
          <LoadingSkeleton rows={6} />
        </FiscalCard>
      </FiscalShell>
    );
  }

  if (revisions.length < 2) {
    return (
      <FiscalShell subtitle={t("fiscalBudgetCompare.subtitle")} toolbar={toolbar}>
        <FiscalCard>
          <p className="text-sm text-neutral-400">
            {t("fiscalBudgetCompare.needTwoRevisionsPre")} {revisions.length}.
          </p>
          <Link
            href="/fiscal/budget"
            className="text-sm text-lime hover:text-lime mt-3 inline-block"
          >
            → {t("fiscalBudgetCompare.goToBudgets")}
          </Link>
        </FiscalCard>
      </FiscalShell>
    );
  }

  function getDiff(
    a: Record<string, unknown> | null,
    b: Record<string, unknown> | null,
    key: string,
  ): { aVal: number | null; bVal: number | null; diffPct: number | null } {
    const aVal = a ? toNumber(a[key] as string | number | null | undefined) : null;
    const bVal = b ? toNumber(b[key] as string | number | null | undefined) : null;
    let diffPct: number | null = null;
    if (aVal !== null && bVal !== null && aVal !== 0) {
      diffPct = ((bVal - aVal) / Math.abs(aVal)) * 100;
    }
    return { aVal, bVal, diffPct };
  }

  return (
    <FiscalShell subtitle={t("fiscalBudgetCompare.subtitle")} toolbar={toolbar}>
      <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-3 grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-wider text-neutral-500 block mb-1">
            {t("fiscalBudgetCompare.leftLabel")}
          </label>
          <select
            value={leftId ?? ""}
            onChange={(e) => setLeftId(Number(e.target.value))}
            className={fiscalInputClass + " w-full"}
          >
            {revisions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
                {r.isActive ? ` · ${t("fiscalBudgetCompare.activeSuffix")}` : ""}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-neutral-500 block mb-1">
            {t("fiscalBudgetCompare.rightLabel")}
          </label>
          <select
            value={rightId ?? ""}
            onChange={(e) => setRightId(Number(e.target.value))}
            className={fiscalInputClass + " w-full"}
          >
            {revisions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
                {r.isActive ? ` · ${t("fiscalBudgetCompare.activeSuffix")}` : ""}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Inputs efetivos */}
      <FiscalCard title={t("fiscalBudgetCompare.inputsTitle")}>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="text-left pb-2">{t("fiscalBudgetCompare.colCategory")}</th>
              <th className="text-right pb-2 font-mono">{left?.name ?? "—"}</th>
              <th className="text-right pb-2 font-mono">{right?.name ?? "—"}</th>
              <th className="text-right pb-2">Δ%</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800">
            {INPUT_LINES.map(({ key, label }) => {
              const { aVal, bVal, diffPct } = getDiff(left, right, key);
              return (
                <tr key={key}>
                  <td className="py-1.5 text-neutral-300">{label}</td>
                  <td className="py-1.5 text-right font-mono">{formatMoney(aVal)}</td>
                  <td className="py-1.5 text-right font-mono">{formatMoney(bVal)}</td>
                  <td
                    className={`py-1.5 text-right font-mono ${
                      diffPct === null
                        ? "text-neutral-500"
                        : diffPct > 0
                          ? "text-lime"
                          : diffPct < 0
                            ? "text-red-400"
                            : "text-neutral-400"
                    }`}
                  >
                    {diffPct === null ? "—" : formatPctSigned(diffPct, { alreadyPct: true })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </FiscalCard>

      {/* KPIs projetados */}
      <FiscalCard title={t("fiscalBudgetCompare.kpisTitle")}>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="text-left pb-2">{t("fiscalBudgetCompare.colKpi")}</th>
              <th className="text-right pb-2 font-mono">{left?.name ?? "—"}</th>
              <th className="text-right pb-2 font-mono">{right?.name ?? "—"}</th>
              <th className="text-right pb-2">Δ%</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800">
            {KPI_FIELDS.map(({ key, label }) => {
              const { aVal, bVal, diffPct } = getDiff(left, right, key);
              return (
                <tr key={key}>
                  <td className="py-1.5 text-neutral-300">{label}</td>
                  <td className="py-1.5 text-right font-mono">{formatMoney(aVal)}</td>
                  <td className="py-1.5 text-right font-mono">{formatMoney(bVal)}</td>
                  <td
                    className={`py-1.5 text-right font-mono ${
                      diffPct === null
                        ? "text-neutral-500"
                        : diffPct > 0
                          ? "text-lime"
                          : diffPct < 0
                            ? "text-red-400"
                            : "text-neutral-400"
                    }`}
                  >
                    {diffPct === null ? "—" : formatPctSigned(diffPct, { alreadyPct: true })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </FiscalCard>

      <p className="text-xs text-neutral-500">
        <Link href="/fiscal/budget" className="text-lime hover:text-lime">
          ← {t("fiscalBudgetCompare.backToBudgets")}
        </Link>
      </p>
    </FiscalShell>
  );
}
